import React, { useEffect, useMemo } from 'react';
import { styled } from '@compiled/react';
import { getIsMultiHCEnabled } from '@helpCenter/util/env';
import { getRelativeBasePath } from '@helpCenter/util/history';
import { getCloudId, getWorkspaceAri } from '@helpCenter/util/meta';
import { graphql, useLazyLoadQuery } from 'react-relay';
import * as grid from 'view/styles/grid';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { Box, xcss } from '@atlaskit/primitives';
import { ExperienceSuccess } from '@atlassian/ufo';
import { multiHcDropdownExperience } from '../multi-hc-dropdown/experiences';
import { NoUnderlineDropdownItemWithAnalytics } from '../no-underline-dropdown-item-with-analytics';
import type { multiHcDropdownContentQuery } from './__generated__/multiHcDropdownContentQuery.graphql';

interface Props {
    toggleSeparatorVisibility: (isVisible: boolean) => void;
    renderQueryError: () => JSX.Element;
    onHelpCenterClick?: () => void;
}

const NUMBER_OF_HELP_CENTERS_TO_FETCH = 100;

export const MultiHcDropdownContent: React.FC<Props> = ({
    toggleSeparatorVisibility,
    renderQueryError,
    onHelpCenterClick,
}) => {
    const data = useLazyLoadQuery<multiHcDropdownContentQuery>(
        graphql`
            query multiHcDropdownContentQuery(
                $cloudId: ID!
                $workspaceAri: ID!
                $NUMBER_OF_HELP_CENTERS_TO_FETCH: Int!
            ) {
                helpCenter(cloudId: $cloudId) {
                    helpCentersList(
                        workspaceAri: $workspaceAri
                        sortOrder: CREATED_DATE_ASCENDING
                        first: $NUMBER_OF_HELP_CENTERS_TO_FETCH
                    ) {
                        ... on HelpCenterQueryResultConnection {
                            edges {
                                node {
                                    ... on HelpCenter {
                                        name {
                                            default
                                        }
                                        slug @optIn(to: "HelpCenterSlugTest")
                                    }
                                }
                            }
                        }
                        ... on QueryError {
                            __typename
                        }
                    }
                }
            }
        `,
        {
            NUMBER_OF_HELP_CENTERS_TO_FETCH,
            cloudId: getCloudId(),
            workspaceAri: getWorkspaceAri(),
        }
    );

    const helpCenters = useMemo(
        () =>
            data.helpCenter?.helpCentersList?.edges
                ?.map((hc) => {
                    if (hc?.node?.slug) {
                        return {
                            hcname: hc?.node?.name?.default || 'Help Center',
                            url: `/helpcenter/${hc?.node?.slug}`,
                        };
                    }
                    //default helpcenter as slug is null
                    return {
                        hcname: hc?.node?.name?.default || 'Help Center',
                        url: '/servicedesk/customer',
                    };
                })
                //not showing the already selected HC in dropdown
                .filter((hc) => hc?.url !== getRelativeBasePath()) || [],
        [data]
    );

    useEffect(() => {
        if (helpCenters.length === 0) {
            toggleSeparatorVisibility(false);
        } else {
            toggleSeparatorVisibility(true);
        }
    }, [helpCenters, toggleSeparatorVisibility]);

    const isDefault = (url: string) => url === '/servicedesk/customer';

    const hasQueryError = data.helpCenter?.helpCentersList?.__typename === 'QueryError';
    return (
        <>
            {hasQueryError ? (
                renderQueryError()
            ) : (
                <StyledDropdownItemGroup isScrollable>
                    {helpCenters.map((hc) => {
                        const isLinkDisabled = !getIsMultiHCEnabled() && !isDefault(hc.url);
                        if (isLinkDisabled) {
                            return (
                                <NoUnderlineDropdownItemWithAnalytics key={hc.url} onClick={onHelpCenterClick}>
                                    {hc.hcname}
                                </NoUnderlineDropdownItemWithAnalytics>
                            );
                        }
                        return (
                            <AnalyticsContext key={hc.url} data={{ actionSubjectId: 'viewHelpCenter' }}>
                                <NoUnderlineDropdownItemWithAnalytics href={hc.url}>
                                    <Box xcss={menuItemStyles}>{hc.hcname}</Box>
                                </NoUnderlineDropdownItemWithAnalytics>
                            </AnalyticsContext>
                        );
                    })}
                    <ExperienceSuccess experience={multiHcDropdownExperience} />
                </StyledDropdownItemGroup>
            )}
        </>
    );
};

const menuItemStyles = xcss({
    color: 'color.text',
    textDecoration: 'none',
    maxWidth: grid.multiple(25).px,
});

const StyledDropdownItemGroup = styled(DropdownItemGroup)({
    maxHeight: grid.multiple(25).px,
});
